import { Content, ContentStack, ContentTable } from "pdfmake/interfaces";
import { CompanyType, loadImage, resizeLogo } from "react-lib/apps/HISV3/common/CompanyLogoForm";
import {
  emptyHospitalContent,
  HospitalAddressMap,
  OADetailType,
} from "react-lib/apps/HISV3/common/HeaderPdfFormTemplate";
import HeaderSapiensForm from "react-lib/apps/HISV3/DPO/sequence/HeaderSapiensForm";

import CONFIG from "config/config";

const color = {
  greyHeader: "#D9D9D9",
  greySummary: "#E8E8E8",
  greyBorder: "#9f9f9f",
  pink: "#F382A0",
  blue: "#095e9d",
};

type ReceiptType = "refund" | "deposit" | "receipt";

type FormSapiensReceiptType = {
  name?: any;
  time?: any;
  code?: string;
  created?: string;
  createdTime?: string;
  hn?: string;
  patient_name?: string;
  // data
  fields?: Record<string, any>[];
  // summary
  totalNonClaimable?: any;
  totalSelfReimbursement?: any;
  totalAmount?: any;
  total_amount_text?: string;
  total_amount_textEN?: string;
  paymentTypeSapient?: string;
  patientNameEN?: string;
  price_text?: string;
  cashonly_sum?: string;
  claimable_sum?: string;
  discount_sum?: string;
  net_sum?: string;
  remark?: string;
  cashier?: string;
  isWatermark?: boolean;
  payment_type?: string;
  receiptType?: string;
  wallet_receipt_codes?: string;
  wallet_deposit_code?: string;
  cancelled_code?: string;
  encounter?: string;
  doctor?: string;
  visit_date?: string;
  coverage?: string;
  discharge_date?: string;
  division?: string;
  pageMargins?: any;
  isPayment?: boolean;
  isShowBackground?: boolean;
  divisionEN?: string;
  coverageEN?: string;
  priceTextEN?: string;
  total_sum?: string;
  headerNawLine?: number;
  oaDetail?: OADetailType;
  sum_total_sum?: string;
  price?: string;
};

const isMEHealth = CONFIG.COMPANY === "MEHEALTH";
const meHealthDivision = ["MeHealth", "MeHealth Medical Lab"];

const extractHn = (hn: string): string => {
  if (!hn) return "";

  const parts = hn.split("_");

  return parts[parts.length - 1];
};

const FormSapiensReceipt = async (props: FormSapiensReceiptType) => {
  var origin = window?.location?.origin;

  const getHospitalAddress = (company: CompanyType, oaDetail?: OADetailType): Content => {
    if (CONFIG.HIDE_COMPANY_ADDRESS_PRINT) {
      return emptyHospitalContent;
    }

    return HospitalAddressMap[company]?.(oaDetail, props?.division) ?? emptyHospitalContent;
  };

  const oaImageFile = props.oaDetail?.imageFile;

  const headerSapiensForm = await HeaderSapiensForm({
    form: "FormReceipt",
    images: {
      oaLogo: oaImageFile || "",
    },
    logoHight:
      CONFIG.COMPANY === "MSK"
        ? 40
        : CONFIG.COMPANY === "RAMASRI"
          ? 45
          : CONFIG.COMPANY === "MEHEALTH"
            ? 45
            : 24,
    pageMargins: props?.pageMargins || [0, 284, 0, 225],
    hospitalAddress: getHospitalAddress(CONFIG.COMPANY as CompanyType, props.oaDetail),
    header: {
      margin: [-8, 0, 0, 0],
      stack: [headerTop(props), isMEHealth ? headerMeBilling(props) : headerSapiens(props)],
    },
    styles: {
      receiptHeader: {
        bold: true,
        fontSize: 8,
      },
      receiptInfo: {
        fontSize: 8,
      },
      receiptHeaderNumber: {
        bold: true,
        fontSize: 8,
        color: isMEHealth ? color.pink : color.blue,
      },
    },
  });

  const fields = props.fields ?? [];
  const remainder = fields.length % 10;
  const numberRows = remainder === 0 ? 0 : 10 - remainder;

  const createRightCell = (value: string | number = "") =>
    tableCell([tableText(String(value), "KanitLM"), tableText("\n")], {
      alignment: "right",
    });

  const fileData = fields.map((item: any) => {
    const { nameTh, nameEn } = splitBillModeName(item.bill_mode__name);

    const displayName = isMEHealth ? item?.item_name || "" : nameTh || "";

    const displayNameEn = isMEHealth
      ? item?.item_name_en
        ? `(${item.item_name_en})`
        : ""
      : nameEn
        ? `(${nameEn})`
        : "";

    const qty = isMEHealth ? "1" : item.total__sum || "";

    const discount = isMEHealth ? item.total__sum : item.discount__sum;

    const net = isMEHealth ? item.total__sum : item.net__sum;

    return [
      tableCell([tableText(displayName, "KanitLM"), tableText(displayNameEn, "KanitLM")], {
        margin: [10, 0, 0, 5],
      }),

      createRightCell(qty),
      createRightCell(discount),
      createRightCell(net),
    ];
  });

  const rowEmpty = Array.from({ length: numberRows }).map(() => [
    {
      stack: [
        { text: " ", font: "KanitLM", style: "tableData" },
        { text: " ", font: "PoppinsLM", style: "tableData" },
      ],
      margin: [10, 0, 0, 5],
      border: [true, false, true, false],
    },
    {
      text: " ",
      font: "PoppinsLM",
      style: "tableData",
      margin: [0, 0, 3, 5],
      border: [true, false, true, false],
    },
    {
      text: " ",
      font: "PoppinsLM",
      style: "tableData",
      margin: [0, 0, 3, 5],
      border: [true, false, true, false],
    },
    {
      text: " ",
      font: "PoppinsLM",
      style: "tableData",
      margin: [0, 0, 3, 5],
      border: [true, false, true, false],
    },
  ]);

  let resized = { width: 400 };

  if (oaImageFile) {
    const img = await loadImage(oaImageFile);
    resized = resizeLogo(img.width, img.height, 280);
  }

  const shouldShowLogo =
    (props.isPayment || props.isShowBackground) &&
    !CONFIG.HIDE_COMPANY_LOGO_PRINT &&
    headerSapiensForm.images?.logo;

  const watermarkBackground = shouldShowLogo
    ? {
        image: "logo",
        opacity: 0.18,
        width: oaImageFile ? resized.width : 400,
        alignment: "center",
        absolutePosition: { y: oaImageFile ? 290 : 400 },
      }
    : {};

  const imgSignatureVenture = await loadImage(
    `${origin}/static/images/meBilling/signature_venture.png`,
  );

  const resizedSignatureVenture = resizeLogo(
    imgSignatureVenture.width,
    imgSignatureVenture.height,
    20,
  );

  return {
    pageSize: "A4",
    ...headerSapiensForm,
    watermark: {
      text: props.isWatermark ? "สำเนา/ Copy" : "",
      font: "KanitLM",
      opacity: 0.2,
      bold: true,
      angle: -15,
      fontSize: 70,
    },
    background: [watermarkBackground],
    content: [
      {
        margin: [7, -11, 7, 0],
        stack: [
          {
            table: {
              headerRows: 2,
              widths: [315, "*", "*", "*"],
              body: [
                [
                  {
                    text: [
                      {
                        text: "รายการ",
                        font: "KanitLM",
                        style: "receiptHeader",
                      },
                      {
                        text: "/Description",
                        font: "PoppinsLM",
                        style: "receiptHeader",
                      },
                    ],
                    alignment: "center",
                    fillColor: color.greyHeader,
                    position: "absolute",
                    relativePosition: { y: 14 },
                    rowSpan: 2,
                  },
                  {
                    text: [
                      {
                        text: "จำนวนเงิน(บาท) ",
                        font: "KanitLM",
                        style: "receiptHeader",
                      },
                      {
                        text: "/Amount (Baht)",
                        font: "PoppinsLM",
                        style: "receiptHeader",
                      },
                    ],
                    alignment: "center",
                    fillColor: color.greyHeader,
                    colSpan: 3,
                  },
                  {},
                  {},
                ],
                [
                  {},
                  {
                    stack: [
                      {
                        text: isMEHealth ? "จำนวน" : "จำนวนเงิน",
                        font: "Kanit",
                        style: "tableData",
                      },
                      {
                        text: isMEHealth ? "(Qty)" : "(Total Amount)",
                        font: "Poppins",
                        style: "tableDataPoppins",
                      },
                    ],
                    alignment: "center",
                    fillColor: color.greyHeader,
                  },
                  {
                    stack: [
                      {
                        text: isMEHealth ? "จำนวนเงินต่อหน่วย" : "ส่วนลด",
                        font: "Kanit",
                        style: "tableData",
                      },
                      {
                        text: isMEHealth ? "(Amount/Unit)" : "(Discount)",
                        font: "Poppins",
                        style: "tableDataPoppins",
                      },
                    ],
                    alignment: "center",
                    fillColor: color.greyHeader,
                  },
                  {
                    stack: [
                      {
                        text: isMEHealth ? "จำนวนเงินรวม" : "จำนวนเงินสุทธิ",
                        font: "Kanit",
                        style: "tableData",
                      },
                      {
                        text: isMEHealth ? "(Total Amount)" : "(Net Amount)",
                        font: "Poppins",
                        style: "tableDataPoppins",
                      },
                    ],
                    alignment: "center",
                    fillColor: color.greyHeader,
                  },
                ],
                ...fileData,
                ...rowEmpty,
              ],
            },
            layout: layoutTable,
          },
        ],
      },
    ],
    styles: {
      ...headerSapiensForm.styles,
      tableData: { fontSize: 8 },
      tableDataPoppins: { fontSize: 6 },
      tableHeaderSummary: {
        bold: true,
        fontSize: 8,
      },
      footerSignature: { fontSize: 9 },
      footerData: { bold: true, fontSize: 6 },
    },
    footer: function (currentPage: number, pageCount: number) {
      return {
        margin: [7, -7, 7, 0],
        stack: isMEHealth ? footerMeBilling(props, resizedSignatureVenture) : footerSapiens(props),
      };
    },
    images: {
      ...headerSapiensForm.images,
      background: origin + "/static/images/logoform/SAPIENS/HeaderSapiensForm.png",
      signatureVenture: origin + "/static/images/meBilling/signature_venture.png",
    },
  };
};

export default FormSapiensReceipt;

/*================================ HANDLES RECEIPT ==============================*/

const receiptMap = {
  refund: {
    th: "ใบคืนเงินมัดจำ",
    en: "(DEPOSIT REFUND)",
  },
  receipt: {
    th: "ใบเสร็จรับเงิน",
    en: "(RECEIPT)",
  },
  deposit: {
    th: "ใบรับเงินมัดจำ",
    en: "(DEPOSIT)",
  },
};

const copyMap = {
  watermark: {
    th: "สำเนา",
    en: "/Copy",
  },
  original: {
    th: "ต้นฉบับ",
    en: "/Original",
  },
} as const;

const receiptNoMap = {
  deposit: {
    label: "/Doc No.  ",
    code: (props: FormSapiensReceiptType) => props.wallet_deposit_code,
  },
  refund: {
    label: "/Doc No.  ",
    code: (props: FormSapiensReceiptType) => props.cancelled_code,
  },
  receipt: {
    label: "/Receipt No.  ",
    code: (props: FormSapiensReceiptType) => props.code,
  },
};

const headerTop = (props: FormSapiensReceiptType) => {
  const receipt = receiptMap[props.receiptType as ReceiptType];
  const receiptNo = receiptNoMap[props.receiptType as ReceiptType];
  const copy = props.isWatermark ? copyMap.watermark : copyMap.original;

  return {
    margin: [0, 15, 0, 0],
    table: {
      widths: [345, "*"],
      body: [
        [
          {
            text: [
              {
                text: receipt?.th,
                font: "KanitLM",
                style: {
                  bold: true,
                  fontSize: 10,
                },
              },
              {
                text: receipt?.en,
                font: "PoppinsLM",
                style: {
                  bold: true,
                  fontSize: 10,
                },
              },
            ],
            alignment: "right",
            border: [false, false, false, false],
          },
          {
            stack: [
              {
                text: [
                  {
                    text: copy.th,
                    font: "KanitLM",
                    style: "receiptHeader",
                  },
                  {
                    text: copy.en,
                    font: "PoppinsLM",
                    style: "receiptHeader",
                  },
                ],
              },
              {
                text: [
                  {
                    text: "เลขที่",
                    font: "KanitLM",
                    style: "receiptHeader",
                  },
                  {
                    text: receiptNo.label,
                    font: "PoppinsLM",
                    style: "receiptHeader",
                  },
                  {
                    text: receiptNo.code(props),
                    font: "PoppinsLM",
                    style: "receiptHeaderNumber",
                  },
                ],
              },
            ],
            alignment: "right",
            border: [false, false, false, false],
            margin: [0, 3, 20, 0],
          },
        ],
        [
          { text: "", border: [false, false, false, false] },
          { text: "", border: [false, false, false, false] },
        ],
        [
          { text: "", border: [false, false, false, false] },
          { text: "", border: [false, false, false, false] },
        ],
      ],
    },
  };
};

const headerLabelCell = (th: string, en: string) => ({
  text: [
    { text: th, font: "KanitLM", style: "receiptHeader" },
    { text: en, font: "PoppinsLM", style: "receiptHeader" },
  ],
  alignment: "left",
  border: [false, false, false, false],
  margin: [20, 0, 0, 0],
});

type HeaderValueType = "text" | "columns";

const headerValueCell = (data: any, type: HeaderValueType = "text") => ({
  alignment: "left",
  border: [false, false, false, false],
  margin: [0, 0, 0, 0],
  ...(type === "columns" ? { columns: data } : { text: data ?? "" }),
});

type TextItemOptions = {
  font?: string;
  style?: string;
  width?: string | number;
  margin?: number[];
};

const textItem = (text: string, options: TextItemOptions = {}) => ({
  text,
  font: options.font ?? "KanitLM",
  style: options.style ?? "receiptInfo",
  ...(options.width ? { width: options.width } : {}),
  ...(options.margin ? { margin: options.margin } : {}),
});

const headerText = (text: any[]) => headerValueCell(text, "text");

const headerColumns = (cols: any[]) => headerValueCell(cols, "columns");

const headerSapiens = (props: FormSapiensReceiptType) => {
  return [
    {
      table: {
        widths: [105, 220, 65, "*"],
        body: [
          [
            headerLabelCell("เลขประจำตัวผู้ป่วย ", "/ HN :"),
            headerText([textItem(props.hn || "", { font: "PoppinsLM" })]),
            headerLabelCell("วันที่ ", "/ Date :"),
            headerColumns([
              textItem(props.created || "", { width: "auto" }),
              textItem(props.time ? `${props.time} น.` : "", { width: "*", margin: [10, 0, 0, 0] }),
            ]),
          ],
          [
            headerLabelCell("ชื่อ ", "/ Patient Name :"),
            headerText([textItem(`${props.name} ${props.patientNameEN || ""}`)]),
            headerLabelCell("EN ", "/ AN :"),
            headerText([textItem(props.encounter || "")]),
          ],
        ],
      },
    },
    {
      table: {
        widths: [106, 219, 135, "*"],
        body: [
          [
            headerLabelCell("แพทย์ ", "/ Doctor Name :"),
            headerText([textItem(props.doctor || "")]),
            headerLabelCell("วันที่เข้ารับการรักษา ", "/ Visit Date :"),
            headerText([textItem(props.visit_date || "")]),
          ],
          [
            headerLabelCell("สิทธิรักษา ", "/ Coverage :"),
            headerText([
              textItem(
                props.coverageEN
                  ? `${props.coverage || ""} (${props.coverageEN || ""})`
                  : props.coverage || "",
              ),
            ]),
            headerLabelCell("วันที่จำหน่าย ", "/ Discharge Date :"),
            headerText([textItem(props.discharge_date || "")]),
          ],
        ],
      },
    },
    {
      table: {
        widths: ["auto", "*"],
        body: [
          [
            headerLabelCell("แผนก ", "/ Division :"),
            headerText([
              textItem(
                props.divisionEN
                  ? `${props.division || ""} (${props.divisionEN || ""})`
                  : props.division || "",
              ),
            ]),
          ],
        ],
      },
    },
  ];
};

const headerMeBilling = (props: FormSapiensReceiptType) => {
  return [
    {
      table: {
        widths: [108, 190, 132, "*"],
        body: [
          [
            headerLabelCell("หมายเลขผู้รับบริการ ", "/ ID :"),
            headerText([textItem(extractHn(props.hn || ""), { font: "PoppinsLM" })]),
            headerLabelCell("วันที่รับบริการ ", "/ Visit Date :"),
            headerText([textItem(props.visit_date || "")]),
          ],
          [
            headerLabelCell("ชื่อ ", "/ Name :"),
            headerText([textItem(`${props.name} ${props.patientNameEN || ""}`)]),
            headerLabelCell("หมายเลขรับบริการ ", "/ VN :"),
            headerText([textItem(props.encounter || "")]),
          ],
          [
            headerLabelCell("ผู้ให้บริการ ", "/ Provider :"),
            headerText([textItem(props.doctor || "")]),
            headerLabelCell("วันที่ชำระเงิน ", "/ Payment date :"),
            headerColumns([
              textItem(props.created || "", { width: "auto" }),
              textItem(props.time ? `${props.time} น.` : "", { width: "*", margin: [10, 0, 0, 0] }),
            ]),
          ],
        ],
      },
    },
  ];
};

/*================================ HOSPITAL ADDRESS ==============================*/

const bilingualTable = (thStack: any[], enStack: any[]): Content => ({
  relativePosition: { x: -10, y: 0 },
  table: {
    body: [
      [
        {
          lineHeight: 1,
          alignment: "left",
          border: [false, false, true, false],
          stack: thStack,
        },
        {
          lineHeight: 1.05,
          alignment: "left",
          margin: [5, 0, 0, 0],
          border: [true, false, false, false],
          stack: enStack,
        },
      ],
    ],
  },
});

/*================================ CONTENT RECEIPT ==============================*/

const tableCell = (
  content: any,
  options: {
    alignment?: "left" | "right";
    margin?: number[];
  } = {},
) => ({
  ...(Array.isArray(content) ? { stack: content } : { text: content }),
  alignment: options.alignment,
  margin: options.margin ?? [0, 0, 3, 5],
  border: [true, false, true, false],
});

const tableText = (text: string = " ", font: "KanitLM" | "PoppinsLM" = "PoppinsLM") => ({
  text,
  font,
  style: "tableData",
});

const splitBillModeName = (name: string = "") => {
  const [nameTh, rest] = name.split(" (");
  const nameEn = rest?.split(")")[0];
  return { nameTh, nameEn };
};

/*================================ FOOTER RECEIPT ==============================*/

const layoutTable = {
  hLineColor: () => color.greyBorder,
  vLineColor: () => color.greyBorder,
};

const thEn = (
  th: string,
  en: string,
  fontTh = "KanitLM",
  fontEn = "PoppinsLM",
  style = "tableHeaderSummary",
) => [
  { text: th, font: fontTh, style },
  { text: `/${en}`, font: fontEn, style },
];

const remarkSection = (props: FormSapiensReceiptType) => {
  const remarkTextTH =
    "เพื่อความปลอดภัยของผู้ป่วย ทางโรงพยาบาลขอสงวนสิทธิ์ไม่รับ เปลี่ยนหรือคืนยาและเวชภัณฑ์";
  const remarkTextEN =
    "For patient safety the hospital reserves the right not to accept any exchange or return of medical supplies.";

  let remarkPrefix = "";
  const headerNawLine = props.headerNawLine ?? 0;

  if (headerNawLine === 0) {
    remarkPrefix = "\n\n\n";
  } else if (headerNawLine === 12) {
    remarkPrefix = "\n\n";
  } else if (headerNawLine === 24) {
    remarkPrefix = "\n";
  } else if (headerNawLine > 36) {
    remarkPrefix = "";
  }

  return [
    {
      stack: [
        {
          text: remarkPrefix + remarkTextTH,
          font: "KanitLM",
          style: "footerData",
        },
        {
          text: remarkTextEN,
          font: "PoppinsLM",
          style: "footerData",
        },
      ],
      margin: [0, 8, 0, 0],
      border: [false, false, false, false],
    },
    {
      text: "",
      border: [false, false, false, false],
    },
  ];
};

const footerSapiens = (props: FormSapiensReceiptType) => {
  const showPayment = props.receiptType === "receipt" || props.receiptType === "deposit";

  return [
    {
      table: {
        widths: [315, "*", "*", "*"],
        body: [
          [
            {
              text: thEn("รวมทั้งสิ้น ", "Total"),
              margin: [10, 5, 0, 5],
              border: [true, true, false, false],
            },
            {
              text: props.cashonly_sum || "0.00",
              font: "Poppins",
              style: "tableData",
              alignment: "right",
              margin: [0, 5, 3, 5],
              border: [false, true, false, false],
            },
            {
              text: props.discount_sum || "0.00",
              font: "Poppins",
              style: "tableData",
              alignment: "right",
              margin: [0, 5, 3, 5],
              border: [false, true, false, false],
            },
            {
              text: props.net_sum || "0.00",
              font: "Poppins",
              style: "tableData",
              alignment: "right",
              margin: [0, 5, 3, 5],
              border: [false, true, true, false],
            },
          ],
        ],
      },
      layout: layoutTable,
    },

    {
      table: {
        widths: [140, "*", 10],
        body: [
          [
            {
              text: "",
              margin: [10, 0, 0, 0],
              border: [true, false, false, false],
            },
            {
              stack: [
                {
                  text: `${props.price_text} (${props.priceTextEN})`,
                  font: "KanitLM",
                  style: "tableData",
                },
              ],
              margin: [5, 0, 0, 0],
              fillColor: color.greySummary,
              border: [false, false, false, false],
            },
            {
              text: "",
              border: [false, false, true, false],
            },
          ],
          [
            {
              text: showPayment
                ? thEn("ชำระเงินโดย ", "Payment")
                : [{ text: " ", font: "KanitLM", style: "tableHeaderSummary" }],
              margin: [10, 0, 0, 0],
              border: [true, false, false, true],
            },
            {
              text: showPayment ? props.payment_type : "",
              font: "KanitLM",
              style: "tableData",
              margin: [0, 0, 0, 0],
              border: [false, false, false, true],
            },
            {
              text: "",
              border: [false, false, true, true],
            },
          ],
          [
            {
              text: [
                {
                  text: "หมายเหตุ ",
                  font: "KanitLM",
                  style: "tableData",
                },
                {
                  text: "/Remark    ",
                  font: "Poppins",
                  style: "tableData",
                },
                {
                  text: props.remark || "",
                  font: "KanitLM",
                  style: "tableData",
                },
              ],
              colSpan: 3,
              margin: [23, 0, 0, 0],
              border: [false, false, false, false],
            },
            {},
            {},
          ],
        ],
      },
      layout: layoutTable,
    },

    {
      table: {
        widths: ["*", 240],
        body:
          props.receiptType === "refund"
            ? [
                [
                  {
                    stack: [
                      {
                        text: "เพื่อความปลอดภัยของผู้ป่วย ทางโรงพยาบาลขอสงวนสิทธิ์ไม่รับ เปลี่ยนหรือคืนยาและเวชภัณฑ์",
                        font: "KanitLM",
                        style: "footerData",
                      },
                      {
                        text: "For patient safety the hospital reserves the right not to accept any exchange or return of medical supplies.",
                        font: "PoppinsLM",
                        style: "footerData",
                      },
                    ],
                    margin: [0, 8, 0, 0],
                    border: [false, false, false, false],
                  },
                  {
                    text: "",
                    border: [false, false, false, false],
                  },
                ],
                [
                  {
                    text: "",
                    border: [false, false, false, false],
                  },
                  {
                    text: "ได้รับเงินไว้เป็นการถูกต้องแล้ว\n\n",
                    font: "KanitLM",
                    style: "footerSignature",
                    alignment: "center",
                    border: [false, false, false, false],
                  },
                ],
                [
                  {
                    stack: [
                      {
                        text: "ลงชื่อ/Signature ____________________ผู้รับเงิน/Cashier",
                        font: "KanitLM",
                        style: "footerSignature",
                        alignment: "center",
                      },
                      {
                        text: `(          ${props.cashier}        )`,
                        font: "KanitLM",
                        style: "footerSignature",
                        alignment: "center",
                      },
                    ],
                    border: [false, false, false, false],
                  },
                  {
                    stack: [
                      {
                        text: "ลงชื่อ/Signature ____________________ผู้รับเงิน",
                        font: "KanitLM",
                        style: "footerSignature",
                        alignment: "center",
                      },
                      {
                        text: `(          ${props.name}        )`,
                        font: "KanitLM",
                        style: "footerSignature",
                        alignment: "center",
                      },
                    ],
                    border: [false, false, false, false],
                  },
                ],
              ]
            : [
                [
                  {
                    text: "",
                    border: [false, false, false, false],
                  },
                  {
                    text: "ได้รับเงินไว้เป็นการถูกต้องแล้ว\n\n",
                    font: "KanitLM",
                    style: "footerSignature",
                    alignment: "center",
                    border: [false, false, false, false],
                  },
                ],
                [
                  {
                    stack: [
                      {
                        text: "ลงชื่อ/Signature ____________________",
                        font: "KanitLM",
                        style: "footerSignature",
                        alignment: "left",
                        margin: [45, 0, 0, 0],
                      },
                      {
                        text: `(          ผู้ป่วย / Patient        )`,
                        font: "KanitLM",
                        style: "footerSignature",
                        alignment: "center",
                      },
                    ],
                    border: [false, false, false, false],
                  },
                  {
                    stack: [
                      {
                        text: "ลงชื่อ/Signature ____________________ผู้รับเงิน/Cashier",
                        font: "KanitLM",
                        style: "footerSignature",
                        alignment: "center",
                      },
                      {
                        text: `(          ${props.cashier}        )`,
                        font: "KanitLM",
                        style: "footerSignature",
                        alignment: "center",
                      },
                    ],
                    border: [false, false, false, false],
                  },
                ],
                remarkSection(props),
              ],
      },
    },
  ];
};

const footerMeBilling = (
  props: FormSapiensReceiptType,
  signature: { width: number; height?: number },
) => {
  const discountSum = parseFloat((props.discount_sum || "0").replace(/,/g, ""));
  const sumTotalSum = parseFloat((props.sum_total_sum || "0").replace(/,/g, ""));
  const price = parseFloat((props.price || "0").replace(/,/g, ""));

  const total = discountSum + (sumTotalSum - price);

  return [
    {
      table: {
        widths: [2, 315, "*", 2],
        body: [
          [
            { text: "", border: [true, true, false, false] },
            {
              text: thEn("ยอดรวมเงิน ", "Subtotal"),
              margin: [10, 5, 0, 5],
              border: [false, true, false, false],
            },
            {
              text: props.sum_total_sum || "0.00",
              font: "Poppins",
              style: "tableHeaderSummary",
              alignment: "right",
              margin: [0, 5, 3, 5],
              border: [false, true, false, false],
            },
            { text: "", border: [false, true, true, false] },
          ],
          [
            { text: "", border: [true, false, false, false] },
            {
              text: thEn("ส่วนลด ", "Discount"),
              margin: [10, 0, 0, 5],
              border: [false, false, false, false],
            },
            {
              text: total.toFixed(2) || "0.00",
              font: "Poppins",
              style: "tableHeaderSummary",
              alignment: "right",
              margin: [0, 0, 3, 5],
              border: [false, false, false, false],
            },
            { text: "", border: [false, false, true, false] },
          ],
          [
            { text: "", border: [true, false, false, false] },
            {
              text: thEn("ยอดรวมสุทธิ ", "Grand Total"),
              margin: [10, 0, 0, 5],
              fillColor: color.greySummary,
              border: [false, false, false, false],
            },
            {
              text: props.price || "0.00",
              font: "Poppins",
              style: "tableHeaderSummary",
              alignment: "right",
              margin: [0, 0, 3, 5],
              fillColor: color.greySummary,
              border: [false, false, false, false],
            },
            { text: "", border: [false, false, true, false] },
          ],
          [
            { text: "", border: [true, false, false, false] },
            {
              columns: [
                {
                  text: "",
                  margin: [10, 0, 0, 5],
                  fillColor: color.greySummary,
                  alignment: "left",
                },
                {
                  text: `${props.price_text} (${props.priceTextEN})`,
                  margin: [0, 0, 3, 5],
                  font: "KanitLM",
                  style: "tableHeaderSummary",
                  alignment: "right",
                },
              ],
              fillColor: color.greySummary,
              border: [false, false, false, false],
              colSpan: 2,
            },
            {},
            { text: "", border: [false, false, true, false] },
          ],
        ],
      },
      layout: layoutTable,
    },

    {
      table: {
        widths: [2, 140, "*", 2],
        body: [
          [
            { text: "", border: [true, false, false, true] },
            {
              text: thEn("ชำระเงินโดย ", "Payment"),
              margin: [10, 0, 3, 0],
              border: [false, false, false, true],
            },
            {
              text: props.payment_type,
              font: "KanitLM",
              style: "tableData",
              margin: [0, 0, 3, 0],
              border: [false, false, false, true],
            },
            { text: "", border: [false, false, true, true] },
          ],
          [
            {
              text: [
                {
                  text: "หมายเหตุ ",
                  font: "KanitLM",
                  style: "tableData",
                },
                {
                  text: "/Remark    ",
                  font: "Poppins",
                  style: "tableData",
                },
                {
                  text: props.remark || "",
                  font: "KanitLM",
                  style: "tableData",
                },
              ],
              colSpan: 4,
              margin: [23, 0, 0, 0],
              border: [false, false, false, false],
            },
            {},
            {},
            {},
          ],
        ],
      },
      layout: layoutTable,
    },

    {
      table: {
        widths: ["*", 240],
        body: [
          [
            { text: "", border: [false, false, false, false] },
            {
              text: "ได้รับเงินไว้เป็นการถูกต้องแล้ว\n\n",
              font: "KanitLM",
              style: "footerSignature",
              alignment: "center",
              border: [false, false, false, false],
            },
          ],
          [
            {
              stack: [
                {
                  text: "ลงชื่อ/Signature ____________________",
                  font: "KanitLM",
                  style: "footerSignature",
                  alignment: "left",
                  margin: [45, 0, 0, 0],
                },
                {
                  text: `(          ผู้รับบริการ/Customer        )`,
                  font: "KanitLM",
                  style: "footerSignature",
                  alignment: "center",
                },
              ],
              border: [false, false, false, false],
            },
            {
              stack: [
                {
                  columns: [
                    {
                      text: "ลงชื่อ/Signature",
                      font: "KanitLM",
                      style: "footerSignature",
                      width: "auto",
                      margin: [0, -20, 0, 0],
                    },

                    {
                      stack: [
                        {
                          image: "signatureVenture",
                          width: signature.width,
                          alignment: "center",
                          margin: [0, -30, 0, -2],
                        },
                        {
                          text: "____________________",
                          font: "KanitLM",
                          style: "footerSignature",
                          alignment: "center",
                          margin: [0, -10, 0, 0],
                        },
                      ],
                      width: "*",
                    },

                    {
                      text: "ผู้รับเงิน/Cashier",
                      font: "KanitLM",
                      style: "footerSignature",
                      width: "auto",
                      margin: [0, -20, 0, 0],
                    },
                  ],

                  columnGap: 0,
                  border: [false, false, false, false],
                },

                meHealthDivision.includes(props.division || "")
                  ? {
                      text: `(          ในนามบริษัท เวนเจอร์ คะตาลิสต์ จำกัด        )`,
                      font: "KanitLM",
                      style: "footerSignature",
                      alignment: "center",
                      margin: [0, -5, 0, 0],
                    }
                  : {
                      text: `(          ${props.cashier}        )`,
                      font: "KanitLM",
                      style: "footerSignature",
                      alignment: "center",
                      margin: [0, -5, 0, 0],
                    },
              ],
              margin: [0, 20, 0, 0],
              border: [false, false, false, false],
            },
          ],
        ],
      },
    },
  ];
};
